import { useEffect, useMemo, useRef, useState } from 'react'

import { getTrait } from '@aytm/app/stats/lib/stat-page'
import { calcLabelClickTarget, isTraitType } from '@aytm/app/stats/page/components/lib/utils'

import { calcDisplayRange } from '@aytm/app/stats/page/question-views/ec/lib/display-range'
import { calcExportData } from '@aytm/app/stats/page/question-views/sh/lib/export-params'
import { productCode } from '@aytm/app/stats/page/question-views/ec/lib/product-code'
import {
  calcReportParamsSection,
  calcResolvedChartMode,
  DEFAULT_CHART_VIEW,
  resolveReportParams
} from '@aytm/app/stats/page/question-views/ec/lib/report-params'
import { PERCENT_CHART_MODES } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

// Owns the ecommerce block state - the chart view, both collapse flags, the shelf view, the
// chart mode, the zoom level and the sort order - seeded from the block's report params and
// re-seeded whenever any of them changes outside the block.
//
// The page view never reads or composes those params itself: it receives an api on mount and
// pulls the payloads it needs (model state, the param set keyed by prefix) when a change is
// announced through onStateChange.
export default function useEcommerceState({
  params,
  format,
  question,
  questionChartMode,
  globalChartMode,
  globalZoomLevel,
  answerVariantsCount,
  defaultSortOrder,
  answer,
  ignoreAmount,
  confidenceLevels,
  labelTarget,
  chartApiRef,
  onReady,
  onStateChange
}) {
  const [state, setState] = useState(() => resolveReportParams({ params, defaultSortOrder }))

  // every input the api payloads are built from, refreshed each render so the pull is current
  const snapshot = useRef()
  snapshot.current = {
    state,
    context: { format, questionChartMode, globalChartMode, globalZoomLevel, answerVariantsCount, defaultSortOrder },
    chart: { question, format, answer, ignoreAmount, confidenceLevels, globalChartMode },
    labelTarget
  }

  const api = useMemo(() => {
    // the rendered chart knows the axis it is showing, zoom included; the maths is the fallback
    // for a chart that has not drawn yet
    const resolveDisplayRange = (chartMode, zoomLevel) => {
      const { question, format, answer, ignoreAmount, confidenceLevels, globalChartMode } = snapshot.current.chart

      if (getTrait() && isTraitType({ ...question, format })) return null

      const extremes = chartApiRef?.current?.getAxisExtremes?.()
      if (null != extremes) return extremes

      return calcDisplayRange({
        chartMode,
        zoomLevel: zoomLevel || snapshot.current.state.zoomLevel || snapshot.current.context.globalZoomLevel,
        globalChartMode,
        viewMode: snapshot.current.state.viewMode,
        format,
        answer,
        ignoreAmount,
        confidenceLevels
      })
    }

    return {
      getState: () => {
        const { viewMode, chartCollapsed, shelfViewMode, shelfCollapsed, sortOrder } = snapshot.current.state
        return { viewMode, chartCollapsed, shelfViewMode, shelfCollapsed, sortOrder }
      },
      getReportParamsSection: () => calcReportParamsSection(snapshot.current.state, snapshot.current.context),
      // pptx / summary bridge: what the block is showing, plus the mode it opens on and the axis
      // that goes with it -- the exporters used to read both off the report params
      getExportData: () => {
        const { state, context } = snapshot.current
        const chartModeResolved = calcResolvedChartMode(state, context)

        return {
          // the chart is described by the mode it draws, which a reader may never have picked
          ...calcExportData({ ...state, chartMode: chartModeResolved }),
          chartModeResolved,
          displayRange: 'pie' === chartModeResolved ? null : resolveDisplayRange(chartModeResolved, null)
        }
      },
      // a product filters by its own code, not by its flat storage position
      getLabelClickTarget: position => {
        const { labelTarget } = snapshot.current
        const av = (labelTarget?.answerVariants || []).find(v => v?.position === position)
        const value = av ? productCode(av) : null
        if (!value) return null

        return { ...calcLabelClickTarget({ ...labelTarget, position }), value }
      },
      getDisplayRange: resolveDisplayRange
    }
  }, [chartApiRef])

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  const sectionKey = JSON.stringify(params ?? null)
  const lastSectionKey = useRef(sectionKey)
  useEffect(() => {
    if (lastSectionKey.current === sectionKey) return
    lastSectionKey.current = sectionKey

    setState(resolveReportParams({ params: JSON.parse(sectionKey), defaultSortOrder }))
  }, [sectionKey, defaultSortOrder])

  // The reason tells the view what the change means: 'seed' is the resolved starting state,
  // 'state' a change the researcher made.
  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  const lastState = useRef(null)
  useEffect(() => {
    const reason = null === lastState.current ? 'seed' : 'state'
    lastState.current = state

    onStateChangeRef.current?.(reason)
  }, [state])

  const handlers = useMemo(
    () => ({
      // Switching the view invalidates the axis: the zoom is dropped, and a percent mode picked
      // for the amount view has no counterpart in the metric views.
      onModelChange: (change = {}) =>
        setState(current => {
          if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

          if (undefined === change.viewMode || change.viewMode === current.viewMode) {
            return { ...current, ...change }
          }

          const dropsChartMode =
            DEFAULT_CHART_VIEW === current.viewMode && PERCENT_CHART_MODES.includes(current.chartMode)

          return {
            ...current,
            ...change,
            chartMode: dropsChartMode ? null : current.chartMode,
            zoomLevel: null
          }
        }),
      onSortChange: sortOrder =>
        setState(current => (current.sortOrder === sortOrder ? current : { ...current, sortOrder })),
      onShelfCollapsedChange: shelfCollapsed => setState(current => ({ ...current, shelfCollapsed })),
      onChartCollapsedChange: chartCollapsed => setState(current => ({ ...current, chartCollapsed })),
      onChartModeChange: chartMode =>
        setState(current => (current.chartMode === chartMode ? current : { ...current, chartMode })),
      onZoomChange: (key, zoomLevel) => setState(current => ({ ...current, zoomLevel }))
    }),
    []
  )

  return { ...state, ...handlers }
}
